import type { ConversationResponse } from './conversationsApi'

/**
 * Fase 4 — separa as duas causas de 403, que pedem reações opostas.
 *
 * Quota é transitória: a vaga volta e "Reabrir conversa" resolve. Namespace
 * divergente é permanente: o token foi emitido num ambiente (`staging:`) e a
 * conversa vive em outro, então nenhuma tentativa vai funcionar. Retentar nesse
 * caso só gera carga — que é justamente o problema que esta migração ataca.
 *
 * A distinção se faz comparando o `identity` do token com o dos participantes
 * da conversa: se **nenhum** participante compartilha o prefixo de ambiente do
 * token, os dois lados não estão no mesmo namespace.
 */

export type PresenceDiagnosis =
  /**
   * Fase 5 — a conversa foi encerrada (409). Reabrir exige `reopen` **antes**
   * do `presence/ensure`: o `reopen` readmite o paciente e limpa `closedAt`,
   * mas não readmite o profissional.
   */
  | { kind: 'closed' }
  | { kind: 'capacity' }
  | {
      kind: 'namespace-mismatch'
      tokenNamespace: string
      conversationNamespace: string
    }
  | { kind: 'unknown' }

/**
 * Prefixo de ambiente da identidade.
 *
 * Formato observado em staging: `staging:patient:<uuid>` — o papel pode estar
 * em qualquer segmento, então o namespace é o que vem **antes** do papel.
 */
export function identityNamespace(identity: string | undefined): string {
  const raw = (identity ?? '').trim()
  if (!raw) return ''
  const partes = raw.split(':')
  if (partes.length <= 2) return ''
  return partes[0]
}

/**
 * Diagnostica um 403 na presença.
 *
 * `capacity` quando o código do erro diz quota. Senão, compara namespaces: só
 * acusa divergência quando há informação dos dois lados — sem participante ou
 * sem identity, o honesto é `unknown`, não um palpite que manda alguém caçar
 * problema de ambiente que talvez não exista.
 */
export function diagnosePresence403(
  errorCode: string | undefined,
  tokenIdentity: string | undefined,
  conversation: Pick<ConversationResponse, 'participants'> | null | undefined
): PresenceDiagnosis {
  // Encerramento vem antes de tudo: sem `reopen` a resposta não muda, e
  // oferecer "tentar de novo" aqui seria um botão que nunca funciona.
  if (errorCode === 'CONVERSATION_CLOSED') {
    return { kind: 'closed' }
  }

  if (
    errorCode === 'PROFESSIONAL_CONVERSATION_LIMIT' ||
    errorCode === 'PATIENT_CONVERSATION_LIMIT'
  ) {
    return { kind: 'capacity' }
  }

  const tokenNs = identityNamespace(tokenIdentity)
  const participantes = conversation?.participants ?? []
  const nsParticipantes = participantes
    .map((p) => identityNamespace(p?.identity))
    .filter((ns) => ns.length > 0)

  if (!tokenNs || nsParticipantes.length === 0) {
    return { kind: 'unknown' }
  }

  if (nsParticipantes.includes(tokenNs)) {
    return { kind: 'unknown' }
  }

  return {
    kind: 'namespace-mismatch',
    tokenNamespace: tokenNs,
    conversationNamespace: nsParticipantes[0],
  }
}

/** Mensagem para a tela. Nunca expõe resposta do servidor nem identidade. */
export function presenceMessageFor(d: PresenceDiagnosis): string {
  switch (d.kind) {
    case 'closed':
      return 'Esta conversa foi encerrada. O histórico continua disponível para leitura.'
    case 'capacity':
      return 'Sem vaga para abrir a conversa agora. O histórico continua disponível para leitura.'
    case 'namespace-mismatch':
      return 'Conversa registrada em outro ambiente. Reabrir não resolve — avise o time técnico.'
    default:
      return 'Não foi possível liberar o envio de mensagens agora. O histórico continua disponível.'
  }
}

/**
 * Namespace divergente é permanente: reabrir não muda nada.
 *
 * `closed` **permite** reabrir, mas por outro caminho — `reopen` e só então
 * `presence/ensure`. Ver `reopenConversation` no hook.
 */
export function podeTentarReabrir(d: PresenceDiagnosis): boolean {
  return d.kind !== 'namespace-mismatch'
}

/** `true` quando reabrir exige `POST /:id/reopen` antes do `presence/ensure`. */
export function exigeReopen(d: PresenceDiagnosis): boolean {
  return d.kind === 'closed'
}
